import type { CalendarWeek } from './api-client';

export type PricePosition = 'overpriced' | 'underpriced' | 'competitive';

export interface WeekSummary {
  ours: number | null;
  oursCount: number;
  /** Our boats priced this week but left out of the median as booked. */
  oursBooked: number;
  market: number | null;
  marketCount: number;
  /** Competitors priced this week but left out of the median as booked. */
  marketBooked: number;
  positionPct: number | null;
  position: PricePosition | null;
}

/**
 * The Competitor Sets summary cards for the week selected on the page: the
 * median price of all our boats in the set against the competitors' median
 * (#258). They used to average the whole season, take "your" price from one
 * boat, and use a plain mean that one mispriced listing could inflate. Within
 * 10% either way is competitive, as on the rest of the page.
 */
export function weekSummary(
  week: Pick<
    CalendarWeek,
    | 'ownMedian'
    | 'ownMedianCount'
    | 'ownMedianBookedCount'
    | 'compSetMedian'
    | 'compSetMedianCount'
    | 'compSetMedianBookedCount'
    | 'medianPositionPct'
  > | null
): WeekSummary {
  if (!week) {
    return {
      ours: null,
      oursCount: 0,
      oursBooked: 0,
      market: null,
      marketCount: 0,
      marketBooked: 0,
      positionPct: null,
      position: null,
    };
  }
  const pct = week.medianPositionPct;
  const position: PricePosition | null =
    pct === null ? null : pct > 10 ? 'overpriced' : pct < -10 ? 'underpriced' : 'competitive';
  return {
    ours: week.ownMedian,
    oursCount: week.ownMedianCount,
    oursBooked: week.ownMedianBookedCount,
    market: week.compSetMedian,
    marketCount: week.compSetMedianCount,
    marketBooked: week.compSetMedianBookedCount,
    positionPct: pct,
    position,
  };
}

/** One median card: its figure, and the line under it. */
export interface MedianCard {
  median: number | null;
  /** No median because every boat priced this week is booked, not for want of a price. */
  allBooked: boolean;
  subtitle: string;
}

/**
 * A median card's line: how many boats the median covers, and how many booked
 * ones it left out. A booked week's price is not an offer (D55), so when every
 * priced boat is booked the card says so instead of showing no price.
 */
export function medianCard(
  median: number | null,
  count: number,
  booked: number,
  boats: (n: number) => string,
  weekLabel: string
): MedianCard {
  if (median === null && booked > 0) {
    return { median, allBooked: true, subtitle: `${boats(booked)} booked · ${weekLabel}` };
  }
  const leftOut = booked > 0 ? ` · ${booked} booked left out` : '';
  return { median, allBooked: false, subtitle: `${boats(count)}${leftOut} · ${weekLabel}` };
}

/** The line under "Your position": the position, or why there is none. */
export function positionNote(summary: WeekSummary): string | undefined {
  if (summary.position) return summary.position.charAt(0).toUpperCase() + summary.position.slice(1);
  if (summary.ours === null && summary.oursBooked > 0) return 'All your boats are booked';
  if (summary.market === null && summary.marketBooked > 0) return 'Every competitor is booked';
  return undefined;
}
